import {
  DEAL_KINDS,
  parseDealStageCatalog,
  STAGE_ACT_ANGLES,
  type DealKind,
} from '@carcheck/shared';

import type { RawRecord } from './rawRecord';

/**
 * AUT-542 — edits of the `deals.stages` form over a copy of the stored
 * records (RULINGS 11). Only the touched field of the touched stage
 * changes; `key`, `selectsCar` and any unknown field travel as stored.
 * `parseDealStageCatalog` is a check before sending — its normalised
 * result is never sent.
 *
 * AUT-633 — the form also writes `requiresAct` («Нужны фото», with the
 * default angles when the step has none of its own), adds a step and
 * opens the «Осмотр» chip only once the stored catalogue has a visible
 * inspection step (RULINGS 7, 23). All functions are pure.
 */

export interface StagePatch {
  label?: string;
  kinds?: DealKind[];
  retired?: boolean;
  requiresAct?: boolean;
}

const STEP_KEY_PREFIX = 'step_';
const STEP_KEY_BYTES = 4;
const HEX_RADIX = 16;
const HEX_BYTE_WIDTH = 2;
const FIRST_ORDER = 1;

/** The api answers a `deals.stages` write that drops a stored step with `stage_removed:<key> (…)`. */
const STAGE_REMOVED_PREFIX = 'stage_removed:';

/** The kind whose chip opens only with a visible inspection step in the stored catalogue. */
const INSPECTION_KIND: DealKind = 'inspection';

const orderOf = (entry: RawRecord | undefined): number =>
  typeof entry?.order === 'number' ? entry.order : Number.POSITIVE_INFINITY;

const isDealKind = (k: string): k is DealKind => (DEAL_KINDS as readonly string[]).includes(k);

/** Stored angles the parser accepts with `requiresAct`: a non-empty list of non-empty strings. */
const hasOwnAngles = (angles: unknown): boolean =>
  Array.isArray(angles) &&
  angles.length > 0 &&
  angles.every((a) => typeof a === 'string' && a !== '');

/** Swap only the `order` values of records `i` and `j` (array positions). */
export const swapStageOrder = (raw: readonly RawRecord[], i: number, j: number): RawRecord[] => {
  const a = raw[i];
  const b = raw[j];
  if (!a || !b || i === j) return [...raw];
  return raw.map((entry, idx) => {
    if (idx === i) return { ...entry, order: b.order };
    if (idx === j) return { ...entry, order: a.order };
    return entry;
  });
};

/**
 * Apply the form's fields to the stage with `key`; every other record is
 * kept as is. Turning «Нужны фото» on writes the six default angles only
 * when the step has no usable angles of its own; turning it off keeps them.
 */
export const setStageField = (
  raw: readonly RawRecord[],
  key: string,
  patch: StagePatch,
): RawRecord[] =>
  raw.map((entry) => {
    if (entry.key !== key) return entry;
    const next: RawRecord = { ...entry };
    if (patch.label !== undefined) next.label = patch.label;
    if (patch.kinds !== undefined) next.kinds = [...patch.kinds];
    if (patch.retired !== undefined) next.retired = patch.retired;
    if (patch.requiresAct !== undefined) {
      next.requiresAct = patch.requiresAct;
      if (patch.requiresAct && !hasOwnAngles(entry.requiredAngles)) {
        next.requiredAngles = [...STAGE_ACT_ANGLES];
      }
    }
    return next;
  });

/**
 * Switch `kind` on or off in the stage's kinds. Stored kinds outside
 * `DEAL_KINDS` are dropped, as the form always did.
 */
export const toggleStageKind = (
  raw: readonly RawRecord[],
  key: string,
  kind: DealKind,
): RawRecord[] => {
  const entry = raw.find((e) => e.key === key);
  if (!entry) return [...raw];
  const current = stageKinds(entry).filter(isDealKind);
  const kinds = current.includes(kind) ? current.filter((k) => k !== kind) : [...current, kind];
  return setStageField(raw, key, { kinds });
};

/**
 * Whether the chip of `kind` can be pressed on `row`. «Осмотр» opens only
 * when the stored catalogue (`saved`, not the draft) has a visible step with
 * `inspection`; a row that already has it can always drop it (RULINGS 7, 23).
 */
export const canToggleKind = (
  saved: readonly RawRecord[],
  row: RawRecord,
  kind: DealKind,
): boolean => {
  if (kind !== INSPECTION_KIND) return true;
  if (stageKinds(row).includes(INSPECTION_KIND)) return true;
  return saved.some(
    (entry) => entry.retired !== true && stageKinds(entry).includes(INSPECTION_KIND),
  );
};

/** «Нужны фото» cannot be turned on for the car-picking step; turning it off is always allowed (RULINGS 14). */
export const canToggleRequiresAct = (row: RawRecord): boolean =>
  row.selectsCar !== true || row.requiresAct === true;

/** Every stage needs a label (not blank) and a kind; the rest is the shared catalogue check. */
export const canSaveStages = (raw: readonly RawRecord[]): boolean => {
  const fieldsSet = raw.every(
    (entry) =>
      typeof entry.label === 'string' &&
      entry.label.trim() !== '' &&
      Array.isArray(entry.kinds) &&
      entry.kinds.length > 0,
  );
  return fieldsSet && parseDealStageCatalog(raw).ok;
};

/** `step_` and 8 random `0-9a-f` characters (RULINGS 1). */
export const makeStageKey = (): string => {
  const bytes = crypto.getRandomValues(new Uint8Array(STEP_KEY_BYTES));
  const hex = Array.from(bytes, (b) => b.toString(HEX_RADIX).padStart(HEX_BYTE_WIDTH, '0'));
  return STEP_KEY_PREFIX + hex.join('');
};

/**
 * Append a new step: exactly `key`, empty `label` and `kinds`, `order` one
 * past the largest integer order (1 in an empty draft), `requiresAct: false`.
 * A key already in the draft (hidden steps included) is drawn again.
 */
export const addStage = (raw: readonly RawRecord[], makeKey: () => string): RawRecord[] => {
  const taken = new Set(raw.map((entry) => entry.key));
  let key = makeKey();
  while (taken.has(key)) key = makeKey();
  const orders = raw
    .map((entry) => entry.order)
    .filter((o): o is number => typeof o === 'number' && Number.isInteger(o));
  const order = orders.length > 0 ? Math.max(...orders) + 1 : FIRST_ORDER;
  return [...raw, { key, label: '', kinds: [], order, requiresAct: false }];
};

/**
 * Whether a server message says the saved stage list lost a step — a sign
 * that another admin saved a newer list while this form was open.
 */
export const isStageRemovedError = (message: string | undefined): boolean =>
  message?.startsWith(STAGE_REMOVED_PREFIX) === true;

/** Array positions of the records in display order (by `order`, stable). */
export const sortedStageIndexes = (raw: readonly RawRecord[]): number[] =>
  raw.map((_, idx) => idx).sort((x, y) => orderOf(raw[x]) - orderOf(raw[y]) || x - y);

/** The stage's kinds as stored (strings only), for the chips. */
export const stageKinds = (entry: RawRecord): string[] =>
  Array.isArray(entry.kinds) ? entry.kinds.filter((k): k is string => typeof k === 'string') : [];
